import { FC, useEffect, useState } from 'react';
import { Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { Box, Skeleton, Text } from '@hopskipdrive/react-components';
import { formatInTimeZone } from 'date-fns-tz';

import { colorTokens } from '../../../../shared/components/variables';
import { LiveAlertDetails } from '../../../types/alert';
import { SrsAvatarStatus } from '../SrsAvatarStatus';

import { AlertsDetailModalProps } from './AlertDetailsModal';
import {
  AlertMessageContainer,
  Container,
  StackedAlertContainer,
  StackedAlertMessageContainer,
  StackedStyledAlert,
  StyledAlert,
} from './LiveAlerts.style';

interface AlertNavDrawerProps {
  isFetching: boolean;
  srsAvailabilityStatus?: string;
  rideAlerts?: LiveAlertDetails[];
  timeZone: string;
}

export function useAlertDetailsContext() {
  return useOutletContext<AlertsDetailModalProps>();
}

export const LiveAlerts: FC<AlertNavDrawerProps> = ({ isFetching, rideAlerts, timeZone }) => {
  const [activeAlertDetails, setActiveAlertDetails] = useState<LiveAlertDetails | undefined>();
  const navigate = useNavigate();
  const { alertId } = useParams();

  useEffect(() => {
    if (alertId && rideAlerts) {
      const alertDetails = rideAlerts.find((alert) => alert.id === Number(alertId));

      setActiveAlertDetails(alertDetails);
    } else {
      setActiveAlertDetails(undefined);
    }
  }, [alertId, rideAlerts]);

  if (isFetching) {
    return (
      <Container>
        <Skeleton data-testid="live-alert-skeleton" variant="text" width={500} />
      </Container>
    );
  }

  const handleShowAlertModal = (event, alertDetails) => {
    event.stopPropagation();
    setActiveAlertDetails(alertDetails);
    navigate(`alert/${alertDetails?.id}`);
  };

  const stackedAlerts = rideAlerts && rideAlerts.length > 1;

  return (
    <Container>
      {activeAlertDetails && (
        <Outlet
          context={{
            alertDetails: activeAlertDetails,
            timeZone: timeZone,
            isFetching: isFetching,
          }}
        />
      )}
      {rideAlerts && rideAlerts.length > 0 ? (
        <>
          {stackedAlerts ? (
            <StackedAlertContainer>
              {rideAlerts.map((rideAlert, index) => (
                <StackedStyledAlert
                  key={index}
                  variant="outlined"
                  severity={rideAlert.highPriority ? 'error' : 'warning'}
                  showSeverity={rideAlert.employeeId === null}
                  onClick={(e) => handleShowAlertModal(e, rideAlert)}
                  message={
                    <StackedAlertMessageContainer>
                      {rideAlert.employeeId !== null && (
                        <Box>
                          <SrsAvatarStatus
                            fullName={`${rideAlerts[0].employeeFirstName} ${rideAlerts[0].employeeLastName}`}
                            size={23}
                            fontSize={12}
                            statusSize={12}
                            key={rideAlert.employeeId?.toString()}
                            status={rideAlert.employeeSrsAvailabilityStatus}
                            pictureUrl={rideAlert.employeeProfilePhotoUrl}
                          />
                        </Box>
                      )}
                      <Box display="flex" padding="3px 8px" flexDirection="column" alignSelf="stretch">
                        <Box display="flex" height="20px">
                          <Text
                            variant="subtitle1"
                            textColor={colorTokens.error.dark}
                            style={{
                              lineHeight: '20px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: `calc(400px / ${rideAlerts.length})`,
                            }}
                          >
                            {rideAlert.typeLabel}
                          </Text>
                        </Box>
                        <Box display="flex" justifyContent="flex-end">
                          <Text variant="caption" textColor="secondaryText">
                            {formatInTimeZone(new Date(rideAlert.createdAt), timeZone, 'h:mmaa zzz')}
                          </Text>
                        </Box>
                      </Box>
                    </StackedAlertMessageContainer>
                  }
                />
              ))}
            </StackedAlertContainer>
          ) : (
            <StyledAlert
              variant="outlined"
              severity={rideAlerts[0].highPriority ? 'error' : 'warning'}
              title={rideAlerts[0].typeLabel}
              onClick={(e) => handleShowAlertModal(e, rideAlerts[0])}
              message={
                <AlertMessageContainer>
                  <Text variant="subtitle2" textColor="secondaryText">
                    {formatInTimeZone(new Date(rideAlerts[0].createdAt), timeZone, 'h:mmaa zzz')}
                  </Text>
                  <Text variant="subtitle2">{rideAlerts[0].employeeId !== null ? 'Assigned to' : 'Unassigned'}</Text>
                  {rideAlerts[0].employeeId !== null && (
                    <Box display="flex" justifyContent="flex-end">
                      <SrsAvatarStatus
                        fullName={`${rideAlerts[0].employeeFirstName} ${rideAlerts[0].employeeLastName}`}
                        size={32}
                        fontSize={14}
                        statusSize={14}
                        key={rideAlerts[0].employeeId?.toString()}
                        status={rideAlerts[0].employeeSrsAvailabilityStatus}
                        pictureUrl={rideAlerts[0].employeeProfilePhotoUrl}
                      />
                      <Text variant="subtitle2">{`${rideAlerts[0].employeeFirstName} ${rideAlerts[0].employeeLastName}`}</Text>
                    </Box>
                  )}
                </AlertMessageContainer>
              }
            />
          )}
        </>
      ) : (
        'No active alerts.'
      )}
    </Container>
  );
};
